import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { User, Lock, Palette, Globe, Sun, Moon, Monitor, Save, Link2, Trash2, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileSettings() {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState({ theme: 'light', language: 'en' });
  const [whiteLabel, setWhiteLabel] = useState({ brand_name: '', logo_url: '', primary_color: '', welcome_text: '' });
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [saving, setSaving] = useState(null);
  const [shortenerConfigs, setShortenerConfigs] = useState([]);
  const [shortenerProviders, setShortenerProviders] = useState([]);
  const [newShortener, setNewShortener] = useState({ provider: '', api_key: '', is_default: false });
  const [addingShortener, setAddingShortener] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      setProfile(data.user);
      setSettings(data.settings);
      setWhiteLabel(prev => ({ ...prev, ...data.white_label }));
      setName(data.user.name || '');
      setTimezone(data.user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  }, [token]);

  const fetchShorteners = useCallback(async () => {
    try {
      const [providersRes, configsRes] = await Promise.all([
        axios.get(`${API}/shorteners/providers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/shorteners/configs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setShortenerProviders(providersRes.data);
      setShortenerConfigs(configsRes.data);
    } catch {}
  }, [token]);

  useEffect(() => { fetchProfile(); fetchShorteners(); }, [fetchProfile, fetchShorteners]);

  const saveProfile = async () => {
    setSaving('profile');
    try {
      await axios.patch(`${API}/profile`, { name, timezone }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Profile updated');
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(null); }
  };

  const changePassword = async () => {
    setSaving('password');
    try {
      await axios.post(`${API}/profile/change-password`, { current_password: currentPw, new_password: newPw }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed');
      setCurrentPw(''); setNewPw('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(null); }
  };

  const saveTheme = async (theme) => {
    setSettings(prev => ({ ...prev, theme }));
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
    try {
      await axios.patch(`${API}/profile/settings`, { theme }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
  };

  const saveLanguage = async (language) => {
    setSettings(prev => ({ ...prev, language }));
    try {
      await axios.patch(`${API}/profile/settings`, { language }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Language updated');
    } catch {}
  };

  const saveWhiteLabel = async () => {
    setSaving('branding');
    try {
      await axios.patch(`${API}/profile/white-label`, whiteLabel, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Branding updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(null); }
  };

  const addShortenerConfig = async () => {
    if (!newShortener.provider || !newShortener.api_key) {
      toast.error('Please select a provider and enter an API key');
      return;
    }
    setAddingShortener(true);
    try {
      await axios.post(`${API}/shorteners/configs`, newShortener, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('URL shortener added!');
      setNewShortener({ provider: '', api_key: '', is_default: false });
      fetchShorteners();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add shortener'); }
    finally { setAddingShortener(false); }
  };

  const deleteShortenerConfig = async (configId) => {
    try {
      await axios.delete(`${API}/shorteners/configs/${configId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Shortener removed');
      fetchShorteners();
    } catch (e) { toast.error('Failed to remove'); }
  };

  const setDefaultShortener = async (configId) => {
    try {
      await axios.patch(`${API}/shorteners/configs/${configId}/default`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Default shortener updated');
      fetchShorteners();
    } catch (e) { toast.error('Failed to update default'); }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <SkipLink /><Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8]" />
        </main>
      </div>
    );
  }

  const isGoogleAuth = profile?.auth_provider === 'google';

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="profile-settings-page">
      <SkipLink /><Header />
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="profile-title">
            Settings
          </h1>

          {/* Profile */}
          <Card className="p-6 border border-[hsl(var(--border))]" data-testid="profile-card">
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5 text-[#1d4ed8]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Profile</h2>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="profile-email">Email</Label>
                <Input id="profile-email" value={profile?.email || ''} disabled className="mt-1 bg-[hsl(var(--muted))]" data-testid="profile-email" />
              </div>
              <div>
                <Label htmlFor="profile-name">Name</Label>
                <Input id="profile-name" value={name} onChange={e => setName(e.target.value)} className="mt-1" data-testid="profile-name" />
              </div>
              <div>
                <Label htmlFor="profile-tz">Timezone</Label>
                <Input id="profile-tz" value={timezone} onChange={e => setTimezone(e.target.value)} className="mt-1" placeholder="e.g. Europe/Athens" data-testid="profile-timezone" />
              </div>
              <Button onClick={saveProfile} disabled={saving === 'profile'} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]" data-testid="save-profile-btn">
                <Save className="h-4 w-4 mr-2" /> {saving === 'profile' ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </Card>

          {/* Password */}
          {!isGoogleAuth && (
            <Card className="p-6 border border-[hsl(var(--border))]" data-testid="password-card">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="h-5 w-5 text-[#1d4ed8]" />
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Change Password</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="current-pw">Current Password</Label>
                  <Input id="current-pw" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="mt-1" data-testid="current-password" />
                </div>
                <div>
                  <Label htmlFor="new-pw">New Password</Label>
                  <Input id="new-pw" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="mt-1" placeholder="Min 6 characters" data-testid="new-password" />
                </div>
                <Button onClick={changePassword} disabled={saving === 'password' || !currentPw || !newPw} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]" data-testid="change-password-btn">
                  <Lock className="h-4 w-4 mr-2" /> {saving === 'password' ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </Card>
          )}

          {/* Theme */}
          <Card className="p-6 border border-[hsl(var(--border))]" data-testid="theme-card">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-[#1d4ed8]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Appearance</h2>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'light', label: 'Light', icon: Sun },
                { id: 'dark', label: 'Dark', icon: Moon },
                { id: 'system', label: 'System', icon: Monitor },
              ].map(opt => {
                const Icon = opt.icon;
                return (
                  <button key={opt.id} onClick={() => saveTheme(opt.id)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      settings.theme === opt.id ? 'border-[#1d4ed8] bg-[#EFF6FF]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--ring))]'
                    }`} data-testid={`theme-${opt.id}`}>
                    <Icon className={`h-6 w-6 mx-auto mb-1 ${settings.theme === opt.id ? 'text-[#1d4ed8]' : 'text-[hsl(var(--muted-foreground))]'}`} />
                    <p className="text-sm font-medium">{opt.label}</p>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Language */}
          <Card className="p-6 border border-[hsl(var(--border))]" data-testid="language-card">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5 text-[#1d4ed8]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Language</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[{ id: 'en', label: 'English' }, { id: 'el', label: 'Greek' }].map(lang => (
                <button key={lang.id} onClick={() => saveLanguage(lang.id)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    settings.language === lang.id ? 'border-[#1d4ed8] bg-[#EFF6FF]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--ring))]'
                  }`} data-testid={`lang-${lang.id}`}>
                  <p className="text-sm font-medium">{lang.label}</p>
                </button>
              ))}
            </div>
          </Card>

          {/* White Label / Branding */}
          <Card className="p-6 border border-[hsl(var(--border))]" data-testid="branding-card">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-[#1d4ed8]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Branding</h2>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Customize how your forms appear to users</p>
            <div className="space-y-3">
              <div>
                <Label htmlFor="brand-name">Brand Name</Label>
                <Input id="brand-name" value={whiteLabel.brand_name} onChange={e => setWhiteLabel(prev => ({ ...prev, brand_name: e.target.value }))}
                  className="mt-1" placeholder="Your Company Name" data-testid="brand-name" />
              </div>
              <div>
                <Label htmlFor="logo-url">Logo URL</Label>
                <Input id="logo-url" value={whiteLabel.logo_url} onChange={e => setWhiteLabel(prev => ({ ...prev, logo_url: e.target.value }))}
                  className="mt-1" placeholder="https://example.com/logo.png" data-testid="logo-url" />
              </div>
              <div>
                <Label htmlFor="primary-color">Primary Color</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={whiteLabel.primary_color || '#1d4ed8'}
                    onChange={e => setWhiteLabel(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-10 h-10 rounded border border-[hsl(var(--border))] cursor-pointer" data-testid="primary-color-picker" />
                  <Input value={whiteLabel.primary_color} onChange={e => setWhiteLabel(prev => ({ ...prev, primary_color: e.target.value }))}
                    placeholder="#1d4ed8" className="flex-1" data-testid="primary-color-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="welcome-text">Custom Welcome Text</Label>
                <Input id="welcome-text" value={whiteLabel.welcome_text} onChange={e => setWhiteLabel(prev => ({ ...prev, welcome_text: e.target.value }))}
                  className="mt-1" placeholder="Welcome! Let's fill out this form together." data-testid="welcome-text" />
              </div>
              {whiteLabel.logo_url && (
                <div className="p-3 rounded-lg bg-[hsl(var(--muted))] flex items-center gap-3" data-testid="brand-preview">
                  <img src={whiteLabel.logo_url} alt="Logo preview" className="h-8 w-8 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                  <span className="font-semibold text-sm" style={whiteLabel.primary_color ? { color: whiteLabel.primary_color } : {}}>
                    {whiteLabel.brand_name || 'Your Brand'}
                  </span>
                </div>
              )}
              <Button onClick={saveWhiteLabel} disabled={saving === 'branding'} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]" data-testid="save-branding-btn">
                <Save className="h-4 w-4 mr-2" /> {saving === 'branding' ? 'Saving...' : 'Save Branding'}
              </Button>
            </div>
          </Card>

          {/* URL Shorteners */}
          <Card className="p-6 border border-[hsl(var(--border))]" data-testid="shortener-card">
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="h-5 w-5 text-[#1d4ed8]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>URL Shorteners</h2>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
              Connect your URL shortener accounts to create compact links for your forms.
            </p>

            {/* Existing configs */}
            {shortenerConfigs.length > 0 && (
              <div className="space-y-2 mb-4">
                {shortenerConfigs.map(cfg => (
                  <div key={cfg.id} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]" data-testid={`shortener-config-${cfg.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm capitalize">{cfg.provider === 'bitly' ? 'Bit.ly' : cfg.provider === 'dubco' ? 'Dub.co' : 'TinyURL'}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Key: {cfg.api_key_preview}</p>
                    </div>
                    {cfg.is_default && (
                      <span className="text-xs bg-[#1d4ed8] text-white px-2 py-0.5 rounded-full">Default</span>
                    )}
                    {!cfg.is_default && (
                      <Button size="sm" variant="ghost" onClick={() => setDefaultShortener(cfg.id)} className="text-[#475569] hover:text-[#1d4ed8]" data-testid={`set-default-shortener-${cfg.id}`}>
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteShortenerConfig(cfg.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-shortener-${cfg.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new config */}
            <div className="space-y-3 p-4 rounded-lg border border-dashed border-[hsl(var(--border))]">
              <p className="text-sm font-medium">Add URL Shortener</p>
              <div>
                <Label htmlFor="shortener-provider">Provider</Label>
                <select
                  id="shortener-provider"
                  value={newShortener.provider}
                  onChange={(e) => setNewShortener(prev => ({ ...prev, provider: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm bg-[hsl(var(--background))]"
                  data-testid="shortener-provider-select"
                >
                  <option value="">Select provider...</option>
                  {shortenerProviders.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="shortener-api-key">API Key</Label>
                <Input
                  id="shortener-api-key"
                  type="password"
                  value={newShortener.api_key}
                  onChange={(e) => setNewShortener(prev => ({ ...prev, api_key: e.target.value }))}
                  className="mt-1"
                  placeholder="Enter your API key"
                  data-testid="shortener-api-key-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="shortener-default"
                  checked={newShortener.is_default}
                  onChange={(e) => setNewShortener(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="rounded border-[hsl(var(--border))]"
                  data-testid="shortener-default-checkbox"
                />
                <Label htmlFor="shortener-default" className="text-sm">Set as default</Label>
              </div>
              <Button
                onClick={addShortenerConfig}
                disabled={addingShortener || !newShortener.provider || !newShortener.api_key}
                className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                data-testid="add-shortener-btn"
              >
                {addingShortener ? 'Adding...' : 'Add Shortener'}
              </Button>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Get API keys from: <a href="https://app.bitly.com/settings/api/" target="_blank" rel="noopener noreferrer" className="text-[#1d4ed8] underline">Bit.ly</a> | <a href="https://app.dub.co/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-[#1d4ed8] underline">Dub.co</a> | <a href="https://tinyurl.com/app/settings/api" target="_blank" rel="noopener noreferrer" className="text-[#1d4ed8] underline">TinyURL</a>
              </p>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
